import { Button } from "@/components/flexkit/Button";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { AutomationCard } from "@/components/automations/AutomationCard";
import { AutomationListFilters } from "@/components/automations/AutomationListFilters";
import {
    isFiltered,
    type AutomationFilters,
    DEFAULT_FILTERS,
    toAPIFilter,
} from "@/components/automations/automation-filters";
import { usePaginatedWorkflows, useGetWorkflow } from "@/queries/automation-queries";
import { workflowDetailsUrl } from "@/utils/automations";
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import type { AutomationTemplate } from "@/utils/automation-templates";
import { AutomationsListEmpty } from "@/components/automations/AutomationsListEmpty";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { AdaptivePagination } from "@/components/flexkit/AdaptivePagination";
import { parseAsInteger, useQueryState } from "nuqs";

type WorkflowListProps = {
    onTemplateSelect: (template: AutomationTemplate) => void;
};

type WorkflowListItemProps = {
    workflowId: string;
};

const WorkflowListItem: FC<WorkflowListItemProps> = ({ workflowId }) => {
    const navigate = useNavigate();
    const { data: workflow, isPending } = useGetWorkflow(workflowId);

    const handleClick = () => {
        if (workflow) {
            navigate(workflowDetailsUrl(workflow));
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Handle Enter and Space for accessibility (standard button behavior)
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
        }
    };

    if (isPending) {
        return <SkeletonBlock ready={false} className="h-[122px]" />;
    }

    if (!workflow) {
        // If we fail to load the automation we just don't show it
        return null;
    }

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={cn("cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset")}
            data-tracking-id="automation-list-item"
        >
            <AutomationCard workflow={workflow} variant="list-item" />
        </div>
    );
};

export const WorkflowList: FC<WorkflowListProps> = ({ onTemplateSelect }) => {
    const listRef = useRef<HTMLDivElement>(null);
    const [filters, setFilters] = useState<AutomationFilters>(DEFAULT_FILTERS);
    const { toast } = useToast();
    const hasShownErrorToast = useRef(false);

    // Page-based pagination state (1-based in URL, converted to 0-based for API)
    const [currentPage, setCurrentPage] = useQueryState("page", parseAsInteger.withDefault(1));

    // Store setCurrentPage in a ref to keep handleFiltersChange stable
    // This prevents infinite re-renders when AutomationListFilters' useEffect triggers
    const setCurrentPageRef = useRef(setCurrentPage);
    setCurrentPageRef.current = setCurrentPage;

    const apiFilter = useMemo(() => toAPIFilter(filters), [filters]);
    const {
        workflowIds,
        error: workflowsError,
        isPending: isPendingWorkflows,
        isFetching,
        refetch: refetchWorkflows,
        totalCount,
        totalPages,
        pageSize,
    } = usePaginatedWorkflows({
        filter: apiFilter,
        page: currentPage - 1,
    });

    const handleFiltersChange = useCallback((newFilters: AutomationFilters) => {
        setFilters(newFilters);
        void setCurrentPageRef.current(1); // reset to 1st page
    }, []);

    useEffect(() => {
        if (workflowsError && !hasShownErrorToast.current) {
            hasShownErrorToast.current = true;
            toast({
                title: "Failed to load automations",
                description: formatError(workflowsError),
            });
        }
        // Reset the flag when error is cleared
        if (!workflowsError && hasShownErrorToast.current) {
            hasShownErrorToast.current = false;
        }
    }, [workflowsError, toast]);

    // Handle keyboard navigation for the workflow list
    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Don't handle if focus is inside a popover (e.g., filter dropdowns)
        const activeElement = document.activeElement;
        if (activeElement?.closest('[role="listbox"], [role="dialog"], [data-radix-popper-content-wrapper]')) {
            return;
        }

        const visibleItems = listRef.current?.querySelectorAll('[role="button"]');
        if (!visibleItems || visibleItems.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            const currentIndex = Array.from(visibleItems).findIndex((item) => item === document.activeElement);
            const nextIndex = currentIndex < visibleItems.length - 1 ? currentIndex + 1 : currentIndex;
            (visibleItems[nextIndex] as HTMLElement)?.focus();
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            const currentIndex = Array.from(visibleItems).findIndex((item) => item === document.activeElement);
            const nextIndex = currentIndex > 0 ? currentIndex - 1 : 0;
            (visibleItems[nextIndex] as HTMLElement)?.focus();
        }
    };

    const hasFilters = isFiltered(filters);

    const showErrorState = !hasFilters && !isPendingWorkflows && workflowsError && workflowIds.length === 0;
    const showEmptyState = !hasFilters && !isPendingWorkflows && !workflowsError && workflowIds.length === 0;

    if (showErrorState) {
        return (
            <EmptyStateCard
                title="Failed to load automations"
                description={
                    workflowsError ? formatError(workflowsError) : "There was an error loading your automations."
                }
                data-testid="workflows-error-state"
            >
                <Button
                    variant="secondary"
                    LeadingIcon={IconRefresh}
                    onClick={() => refetchWorkflows()}
                    data-tracking-id="retry-load-workflows"
                >
                    Retry
                </Button>
            </EmptyStateCard>
        );
    }

    if (showEmptyState) {
        return <AutomationsListEmpty onTemplateSelect={onTemplateSelect} />;
    }

    return (
        <div className="flex flex-col gap-3 @container" data-testid="workflows-list" onKeyDown={handleKeyDown}>
            <AutomationListFilters onFiltersChange={handleFiltersChange} />

            {isPendingWorkflows ? (
                <div className="flex flex-col gap-2">
                    <SkeletonBlock ready={false} animate={false} className="h-[122px] rounded-xl opacity-55" />
                    <SkeletonBlock ready={false} animate={false} className="h-[122px] rounded-xl opacity-35" />
                    <SkeletonBlock ready={false} animate={false} className="h-[122px] rounded-xl opacity-15" />
                </div>
            ) : workflowIds.length === 0 && hasFilters ? (
                <EmptyStateCard
                    title="No matching automations"
                    description={
                        filters.search
                            ? `No automations match "${filters.search}". Try a different search term.`
                            : "No automations match the selected filters."
                    }
                    data-testid="no-matching-automations-empty-state"
                />
            ) : (
                <div
                    ref={listRef}
                    className="flex flex-col divide-y divide-border-base overflow-clip rounded-xl border border-border-base bg-surface-primary shadow-sm [&>*:first-child]:rounded-t-xl [&>*:last-child]:rounded-b-xl"
                    data-testid="workflows-list-items"
                >
                    {workflowIds.map((workflowId) => (
                        <WorkflowListItem key={workflowId} workflowId={workflowId} />
                    ))}
                </div>
            )}

            <AdaptivePagination
                className="px-2 pb-0 pt-2 sm:px-2"
                totalCount={totalCount}
                totalPages={totalPages}
                currentPage={currentPage - 1}
                onPageChange={(page) => {
                    void setCurrentPage(page + 1);
                }}
                isLoading={isFetching}
                currentItemsCount={workflowIds.length}
                pageSize={pageSize}
            />
        </div>
    );
};
